import { useEffect, useState } from "react";
import { MapPin, Loader2, TriangleAlert } from "lucide-react";

import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { joinParts, plural } from "../../lib/vocabulary";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

/**
 * Bulk zone assignment for the doctors with no territory unit. D2 / RC-22.
 *
 * THE RULE THAT SHAPES ALL OF THIS: **a suggestion is never applied silently.**
 * The backend derives a zone from the doctor's address and says WHY; this
 * surface shows the suggestion and its evidence, pre-selects it in an editable
 * dropdown, and applies nothing until the operator ticks the row and presses
 * the button. A pre-selected dropdown is an offer; a saved value is a decision,
 * and the gap between them is the whole point.
 *
 * NOTHING IS GUESSED INTO A BLANK. A doctor whose address suggests nothing
 * shows "no suggestion" with an empty picker — never a default zone, which
 * would be a guess wearing a decision's clothes. A doctor whose address names
 * two zones shows AMBIGUOUS and the two candidates, and is also left blank:
 * the operator picks, or nobody does.
 *
 * The zone list is backend-served with the rest of the payload, so this picker
 * cannot hold a stale copy of the four Patna zones (#78).
 */
export default function TerritorySuggestionPanel({ onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [choice, setChoice] = useState({});     // doctorId -> unit id
  const [ticked, setTicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: payload } = await api.get("/doctors/needs-territory");
        if (cancelled) return;
        setData(payload);
        // Pre-SELECT the suggestion; do not pre-TICK it. The distinction is
        // the difference between offering an answer and making one.
        // THE PAYLOAD'S OWN SHAPE: `suggestion.unit` is the unit NAME and
        // `suggestion.unit_id` is its id — see `territory_suggestions` in
        // routes_doctors.py, and the fixture in __fixtures__/ which is
        // generated FROM that handler so this cannot drift again. The first
        // version of this file read `suggestion.unit.id`, which is `undefined`
        // on a string: every checkbox stayed disabled and the panel could not
        // assign anything. Its test passed because its fixture had been
        // invented to match the component instead of the endpoint.
        const seeded = {};
        for (const row of payload.items || []) {
          if (row.suggestion?.unit_id) seeded[row.id] = row.suggestion.unit_id;
        }
        setChoice(seeded);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-callisto-muted"
         data-testid="territory-suggestions-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Reading addresses…
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-red-700" data-testid="territory-suggestions-error">
        Suggestions could not be loaded — {error}. No doctor has been changed.
      </p>
    );
  }
  const items = Array.isArray(data?.items) ? data.items : [];
  const zones = Array.isArray(data?.zones) ? data.zones : [];
  if (!data || data.total === 0 || items.length === 0) return null;

  // F3: `zones` is the four Patna zones, but a suggestion can name any active
  // unit. A picker that cannot represent its own suggestion would either lose
  // it or post an id the endpoint refuses, so the suggested unit is added to
  // that row's options — the same rule as TerritoryUnitSelect showing a value
  // the master no longer lists.
  const allUnits = [
    ...zones,
    ...items
      .map((r) => r.suggestion)
      .filter((sg) => sg?.unit_id && !zones.some((z) => z.id === sg.unit_id))
      .map((sg) => ({ id: sg.unit_id, name: sg.unit, outside: true })),
  ];
  function optionsFor(row) {
    const sg = row.suggestion;
    if (!sg?.unit_id || zones.some((z) => z.id === sg.unit_id)) return zones;
    return [{ id: sg.unit_id, name: `${sg.unit} (not a Patna zone)` }, ...zones];
  }

  const applicable = [...ticked].filter((id) => choice[id]);

  async function apply() {
    setBusy(true);
    try {
      // One call per unit, using the bulk endpoint that already exists — the
      // operator's ticks, never the suggestions.
      const byUnit = {};
      for (const id of applicable) (byUnit[choice[id]] ||= []).push(id);
      // F4: track what actually landed. A failure on the second unit must not
      // leave the first unit's rows listed as unapplied — "nothing happened"
      // and "some of it happened" are different states, and the panel is not
      // entitled to report the first when the second is true.
      const done = new Set();
      try {
        for (const [unitId, ids] of Object.entries(byUnit)) {
          const unit = allUnits.find((z) => z.id === unitId);
          if (!unit) throw new Error(`no unit named for ${unitId}`);
          // PUT /doctors/bulk/territory — the route BulkTerritoryBar already calls.
        // This said POST /doctors/bulk-territory: wrong verb AND wrong path,
        // 405 live (the path only "matched" by falling into GET /doctors/{id}
        // as a path param), and green in tests because the mock asserted the
        // dead URL — the second instance of the unwired-mock class Block 5's
        // sweep found in one day. Two callers of one backend route had
        // diverged; now both spell it the same way.
        await api.put("/doctors/bulk/territory", {
            doctor_ids: ids, territory_unit: unit.name,
          });
          ids.forEach((id) => done.add(id));
        }
      } finally {
        if (done.size) {
          onApplied?.(done.size);
          setData((prev) => ({
            ...prev,
            items: (prev.items || []).filter((r) => !done.has(r.id)),
            total: prev.total - done.size,
          }));
          setTicked(new Set());
        }
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50/60 p-3"
             data-testid="territory-suggestions">
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-callisto-navy flex items-center gap-1.5">
            <MapPin size={14} /> {plural(data.total, "doctor")} with no territory unit
          </h3>
          <p className="text-[11px] text-callisto-muted mt-0.5"
             data-testid="territory-suggestions-counts">
            {joinParts([
              `${data.suggested} with a suggestion`,
              data.ambiguous ? `${data.ambiguous} ambiguous` : null,
              data.no_suggestion ? `${data.no_suggestion} with nothing to go on` : null,
            ])}
            {" — nothing is applied until you tick it."}
          </p>
        </div>
        <Button size="sm" disabled={busy || applicable.length === 0} onClick={apply}
                data-testid="territory-suggestions-apply">
          {busy ? "Applying…" : `Apply ${applicable.length} selected`}
        </Button>
      </header>

      <ul className="mt-3 space-y-2">
        {items.map((row) => (
          <li key={row.id} className="flex items-start gap-2 text-xs"
              data-testid={`territory-suggestion-${row.id}`}>
            <input
              type="checkbox"
              className="mt-1"
              checked={ticked.has(row.id)}
              disabled={!choice[row.id]}
              onChange={(e) => {
                const next = new Set(ticked);
                e.target.checked ? next.add(row.id) : next.delete(row.id);
                setTicked(next);
              }}
              aria-label={`Assign ${row.name}`}
              data-testid={`territory-suggestion-tick-${row.id}`}
            />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-callisto-navy truncate">{row.name}</p>
              <p className="text-[11px] text-callisto-muted truncate">
                {joinParts([row.address, row.city, row.pin_code], ", ")}
              </p>
              {/* THE EVIDENCE. A suggestion the operator cannot check is an
                  instruction, and this screen is not entitled to give one. */}
              <p className={`text-[11px] mt-0.5 ${row.suggestion?.unit ? "text-callisto-body" : "text-amber-700"}`}
                 data-testid={`territory-suggestion-why-${row.id}`}>
                {row.suggestion?.unit ? null : <TriangleAlert size={10} className="inline mr-1" />}
                {/* Never the raw reason key: `no_suggestion` is a wire value,
                    not a sentence, and the operator reading this row is being
                    asked to make a decision on it. */}
                {row.suggestion?.evidence
                  || (row.suggestion?.reason === "ambiguous"
                      ? "The address names more than one zone, so nothing is suggested."
                      : "No suggestion — nothing in the address names a zone.")}
              </p>
            </div>
            <Select
              value={choice[row.id] || ""}
              onValueChange={(v) => setChoice({ ...choice, [row.id]: v })}
            >
              <SelectTrigger className="h-8 w-40 shrink-0"
                             data-testid={`territory-suggestion-select-${row.id}`}>
                <SelectValue placeholder="Pick a zone" />
              </SelectTrigger>
              <SelectContent>
                {optionsFor(row).map((z) => (
                  <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </li>
        ))}
      </ul>
    </section>
  );
}
